import Axios from 'axios';
import config from '../config/config.js';

// util.format 대체 함수 (브라우저 환경용)
const formatString = (str, ...params) => {
    let result = str;
    params.forEach(param => {
        result = result.replace('%s', param);
    });
    return result;
};

const makeUrl = (url, params) => {
    // 개발 환경(localhost)에서는 프록시 사용 (상대 경로)
    // 프로덕션 환경에서는 절대 경로 사용
    const isDevelopment =
        process.env.NODE_ENV === 'development' ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';

    let result = isDevelopment ? url : config.API_BASE_URL + url;

    if (params != null) {
        result = formatString(result, ...params);
    }
    console.log('url: ' + result);
    return result;
};

// 전역 로딩 카운터 초기화
if (typeof global !== 'undefined') {
    global.loadingCount = global.loadingCount || 0;
} else {
    window.loadingCount = window.loadingCount || 0;
}

const loadingStart = () => {
    const loadingCount = typeof global !== 'undefined' ? global.loadingCount : window.loadingCount;
    const newCount = loadingCount + 1;

    if (typeof global !== 'undefined') {
        global.loadingCount = newCount;
    } else {
        window.loadingCount = newCount;
    }

    if (newCount === 1) {
        // 로딩 UI 표시 로직 (필요시 구현)
        console.log('Loading started...');
    }
};

const loadingEnd = async () => {
    try {
        const loadingCount = typeof global !== 'undefined' ? global.loadingCount : window.loadingCount;
        if (loadingCount <= 1) {
            // 로딩 UI 숨김 로직 (필요시 구현)
            console.log('Loading ended...');
        }

        const newCount = Math.max(0, loadingCount - 1);
        if (typeof global !== 'undefined') {
            global.loadingCount = newCount;
        } else {
            window.loadingCount = newCount;
        }
    } catch (e) {
        console.error('Loading end error:', e);
    }
};

// 토큰 갱신 중복 방지 플래그
let isRefreshing = false;
let refreshSubscribers = [];

// 토큰 갱신 대기 중인 요청들을 처리하는 함수
const subscribeTokenRefresh = (cb) => {
    refreshSubscribers.push(cb);
};

const onRefreshed = (token) => {
    refreshSubscribers.forEach((cb) => cb(token));
    refreshSubscribers = [];
};

// 토큰 갱신 함수
const refreshAccessToken = async () => {
    try {
        const refreshToken = localStorage.getItem('refreshToken');
        console.log('🔍 현재 저장된 refreshToken:', refreshToken ? `${refreshToken.substring(0, 20)}...` : 'null');
        
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        console.log('🔄 토큰 갱신 시도...');
        console.log('📤 요청 데이터:', { refreshToken: `${refreshToken.substring(0, 20)}...` });
        
        const response = await Axios.post(
            makeUrl('/user/auth/token/refresh'),
            { refreshToken },
            {
                withCredentials: true,
                timeout: 30000,
                headers: {
                    Accept: 'application/json; charset=utf-8',
                    'Content-Type': 'application/json; charset=utf-8',
                },
            }
        );

        console.log('📥 서버 응답 전체:', response);
        console.log('📥 응답 데이터:', response.data);
        
        // 서버 응답 구조: { result: { accessToken, refreshToken }, code, message }
        const result = response.data.result || response.data;
        const { accessToken, refreshToken: newRefreshToken } = result;
        
        console.log('🔍 파싱된 토큰:', {
            accessToken: accessToken ? `${accessToken.substring(0, 20)}...` : 'null',
            newRefreshToken: newRefreshToken ? `${newRefreshToken.substring(0, 20)}...` : 'null'
        });
        
        if (accessToken) {
            localStorage.setItem('accessToken', accessToken);
            if (newRefreshToken) {
                localStorage.setItem('refreshToken', newRefreshToken);
            }
            console.log('✅ 토큰 갱신 성공');
            return accessToken;
        }
        
        throw new Error('No access token in response');
    } catch (error) {
        console.error('❌ 토큰 갱신 실패:', error);
        console.error('❌ 에러 응답:', error.response?.data);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        throw error;
    }
};

// ✅ 매 요청마다 토큰 불러와서 config 만들어주는 함수
const getRequestConfig = async (customHeaders = {}) => {
    const token = localStorage.getItem('accessToken');
    return {
        withCredentials: true,
        timeout: 30000, // 30초 타임아웃
        headers: {
            Accept: 'application/json; charset=utf-8',
            'Content-Type': 'application/json; charset=utf-8',
            ...(token ? { Authorization: `Bearer ${token}` } : {}), // ✅ 토큰 있으면 추가
            ...customHeaders, // ✅ 커스텀 헤더 추가 (parentToken 등)
        },
    };
};

// 로그아웃 처리 중복 방지 플래그
let isLoggingOut = false;

// 로그아웃 처리 함수
const handleLogout = () => {
    if (isLoggingOut) return;
    isLoggingOut = true;
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    
    alert("세션이 만료되었습니다. 다시 로그인해주세요.");
    
    // 현재 경로 확인 후 로그인 페이지로 리다이렉트 (무한 루프 방지)
    if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
    }
};

// Axios Interceptor 설정 - 401 에러 시 자동 토큰 갱신
Axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // 401 에러이고, 아직 재시도하지 않은 요청인 경우
        if (error.response?.status === 401 && !originalRequest._retry) {
            // 토큰 갱신 API 자체가 실패한 경우는 재시도하지 않음
            if (originalRequest.url?.includes('/user/auth/token/refresh')) {
                handleLogout();
                return Promise.reject(error);
            }

            // ✅ 대리인 결제 요청(_skipAuthRefresh 플래그)은 토큰 갱신 건너뛰기
            if (originalRequest._skipAuthRefresh) {
                console.log('## 대리인 결제 요청 - 토큰 갱신 건너뛰기');
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            // 이미 토큰 갱신 중인 경우
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    subscribeTokenRefresh((token) => {
                        if (token) {
                            originalRequest.headers['Authorization'] = `Bearer ${token}`;
                            resolve(Axios(originalRequest));
                        } else {
                            reject(error);
                        }
                    });
                });
            }

            isRefreshing = true;

            try {
                const newToken = await refreshAccessToken();
                isRefreshing = false;
                onRefreshed(newToken);

                // 원래 요청 재시도
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return Axios(originalRequest);
            } catch (refreshError) {
                isRefreshing = false;
                onRefreshed(null);
                refreshSubscribers = [];
                
                // 토큰 갱신 실패 시 로그아웃 처리
                handleLogout();
                
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

const httpGet = async (url, params, data, hideLoading, customHeaders) => {
    console.log("++++++++++++++httpGet=" + url, params, data);
    if (!hideLoading) loadingStart();

    try {
        const config = await getRequestConfig(customHeaders);
        const fullUrl = makeUrl(url, params);
        console.log("httpGet - Full URL:", fullUrl);
        console.log("httpGet - Config:", config);
        console.log("httpGet - Params:", data || {});

        // ✅ customHeaders에 Authorization이 있으면 대리인 결제로 간주하여 토큰 갱신 건너뛰기
        const axiosConfig = {
            ...config,
            params: data || {}
        };
        if (customHeaders?.Authorization) {
            axiosConfig._skipAuthRefresh = true;
        }

        const response = await Axios.get(fullUrl, axiosConfig);
        if (!hideLoading) loadingEnd();
        console.log("httpGet - Response:", response.data);
        return response.data;
    } catch (error) {
        if (!hideLoading) loadingEnd();
        console.error("httpGet - Error details:", {
            message: error.message,
            code: error.code,
            response: error.response,
            request: error.request,
            config: error.config
        });
        return errorReject(hideLoading, null, url)(error);
    }
};

const httpPost = async (url, params, data, hideLoading) => {
    console.log('========== httpPost 시작 ==========');
    console.log('httpPost - URL:', url);
    console.log('httpPost - Params:', params);
    console.log('httpPost - Data:', JSON.stringify(data, null, 2));
    console.log('httpPost - hideLoading:', hideLoading);

    if (!hideLoading) loadingStart();

    try {
        const config = await getRequestConfig();
        console.log('httpPost - Config:', config);

        const fullUrl = makeUrl(url, params);
        console.log('httpPost - Full URL:', fullUrl);
        console.log('httpPost - 🚀 Axios.post 호출 직전');

        const response = await Axios.post(fullUrl, data, config);

        console.log('httpPost - ✅ 응답 받음:', response);
        console.log('httpPost - Response data:', response.data);
        console.log('httpPost - Response status:', response.status);

        if (!hideLoading) loadingEnd();
        console.log('========== httpPost 성공 종료 ==========');
        return response.data;
    } catch (error) {
        console.error('httpPost - ❌ 에러 발생:', error);
        console.error('httpPost - Error message:', error.message);
        console.error('httpPost - Error response:', error.response);
        if (!hideLoading) loadingEnd();
        console.log('========== httpPost 에러 종료 ==========');
        return errorReject(hideLoading, null, url)(error);
    }
};

const httpPut = async (url, params, data, hideLoading) => {
    console.log("++++++++++++++httpPut=" + url);
    if (!hideLoading) loadingStart();

    try {
        const config = await getRequestConfig();
        const response = await Axios.put(makeUrl(url, params), data, config);
        if (!hideLoading) loadingEnd();
        return response.data;
    } catch (error) {
        if (!hideLoading) loadingEnd();
        return errorReject(hideLoading, null, url)(error);
    }
};

const imageUrl = (idx) => {
    return `${config.API_BASE_URL}/file/${idx}`;
};

const serverUrl = config.API_BASE_URL;

const httpUrl = {
    bannerList: (type, offset = 0, limit = 20) =>
        `/user/banners?type=${type}&offset=${offset}&limit=${limit}`,
    bannerClick: '/user/banners/click',  // POST: 배너 클릭 통계 (body: { bannerId })
    qrCode: '/user/qr-code/%s',
    updateOrder: '/user/order/update',  // POST: 주문 정보 업데이트 (사용자, 좌석 등 추가 정보)
    usableCoupons: '/user/usable/coupons?storeId=%s&passId=%s&price=%s',
    verifyParentCoupon: '/user/payment/usable/coupons',  // GET: 대리인 결제 쿠폰 조회 (쿼리: storeId, passId, price, parentToken)
    completePay: '/user/payment/order',  // POST: 결제 완료 처리 (대리인 결제용)
    verifyParent: '/user/payment/verify-parent',  // POST: 보호자 인증 (대리인 결제)
    getOrderInfo: (orderNumber) => `/user/draft/order?orderNumber=${orderNumber}`,  // GET: 주문 정보 조회 (orderNumber로 조회)
    draftOrder: '/user/payment/draft/order',  // POST: draft order 생성/업데이트 (대리인 결제용)
    refreshToken: '/user/auth/token/refresh',  // POST: 액세스 토큰 갱신 (리프레시 토큰 사용)
    // 안내사항
    userGuide: '/user/guide?type=%s&storeId=%s',

};

export {
    httpGet,
    httpPost,
    httpPut,
    httpUrl,
    imageUrl,
    serverUrl
};

function errorReject(hideLoading, reject, url) {
    return error => {
        console.log('## http error: ' + url + " - " + JSON.stringify(error, null, 4));

        const status = error?.response?.status;
        const statusText = error?.response?.statusText;
        const responseData = error?.response?.data;
        const isNetworkError = error?.message === 'Network Error' || error?.code === 'NETWORK_ERROR' || error?.code === 'ERR_NETWORK';

        console.log('## 상세 오류 정보:', {
            status,
            statusText,
            responseData,
            url,
            message: error?.message,
            code: error?.code,
            isNetworkError,
            request: error?.request,
            config: error?.config
        });

        // Network Error 특별 처리
        if (isNetworkError) {
            console.error('## Network Error 상세 분석:', {
                url: url,
                fullUrl: makeUrl(url),
                hasResponse: !!error?.response,
                hasRequest: !!error?.request,
                message: error?.message,
                code: error?.code,
                stack: error?.stack
            });

            alert(`네트워크 연결 오류가 발생했습니다.\n\n확인사항:\n1. 인터넷 연결 상태\n2. 서버 상태 (${makeUrl(url)})\n3. CORS 설정\n4. 방화벽 설정\n\nURL: ${url}\n에러: ${error?.message}`);
        } else if (status === 401) {
            // 401 에러는 Axios Interceptor에서 처리하므로 여기서는 로그만 남김
            console.log('## 401 에러는 Interceptor에서 처리됨');
        } else if (status === 404) {
            // 404 에러는 조용히 처리 (선택적 데이터는 없어도 앱이 동작해야 함)
            console.warn(`## 404 Not Found: ${url}`);
        } else if (status >= 500) {
            alert(`서버 내부 오류가 발생했습니다.\nURL: ${url}\n상태: ${status} ${statusText}`);
        } else if (status === 400) {
            const errorMsg = responseData?.message || error?.message || '잘못된 요청입니다.';
            alert(`잘못된 요청입니다.\n${errorMsg}\nURL: ${url}\n상태: ${status}`);
        } else {
            const errorMsg = responseData?.message || error?.message || '알 수 없는 오류';
            alert(`서버 요청 오류\n${errorMsg}\nURL: ${url}\n상태: ${status || 'Unknown'}`);
        }

        if (reject) reject(error);
        throw error; // 호출한 쪽에서도 잡을 수 있게 던짐
    };
}
